import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Box from '@cloudscape-design/components/box';
import Modal from '@cloudscape-design/components/modal';
import Link from '@cloudscape-design/components/link';
import Flashbar from '@cloudscape-design/components/flashbar';
import Icon from '@cloudscape-design/components/icon';
import {
  fetchLibraryEntries,
  updateLibraryEntryStatus,
  updateLibraryEntryNotes,
  deleteLibraryEntry,
  LibraryEntryListItem,
} from '../services/api';

/** Inline-editable notes cell */
const NotesCell: React.FC<{
  item: LibraryEntryListItem;
  onSave: (templateId: string, notes: string) => Promise<void>;
}> = ({ item, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.notes ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(item.templateId, value);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(item.notes ?? '');
    setEditing(false);
  };

  if (editing) {
    return (
      <SpaceBetween direction="horizontal" size="xxs">
        <Input
          value={value}
          onChange={({ detail }) => setValue(detail.value)}
          placeholder="Add a note..."
          disabled={saving}
          autoFocus
          onKeyDown={(e: any) => {
            if (e.detail?.key === 'Enter') handleSave();
            if (e.detail?.key === 'Escape') handleCancel();
          }}
        />
        <Link onFollow={(e) => { e.preventDefault(); handleSave(); }}>
          {saving ? '...' : 'Save'}
        </Link>
        <Link onFollow={(e) => { e.preventDefault(); handleCancel(); }} variant="secondary">
          Cancel
        </Link>
      </SpaceBetween>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      style={{ cursor: 'pointer', minWidth: 80, display: 'inline-block' }}
    >
      {item.notes ? (
        <span>{item.notes} <Icon name="edit" size="small" /></span>
      ) : (
        <Link onFollow={(e) => { e.preventDefault(); setEditing(true); }} variant="secondary" fontSize="body-s">
          Add note
        </Link>
      )}
    </span>
  );
};

const ApprovalLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LibraryEntryListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortDescending, setSortDescending] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<LibraryEntryListItem | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [flashItems, setFlashItems] = useState<{ id: string; type: 'success' | 'error'; content: string }[]>([]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLibraryEntries();
      setEntries(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load library entries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const sortedEntries = [...entries].sort((a, b) => {
    const dateA = new Date(a.approvalDate).getTime();
    const dateB = new Date(b.approvalDate).getTime();
    return sortDescending ? dateB - dateA : dateA - dateB;
  });

  const addFlash = (type: 'success' | 'error', content: string) => {
    const id = Date.now().toString();
    setFlashItems((prev) => [...prev, { id, type, content }]);
  };

  const handleToggleStatus = async (entry: LibraryEntryListItem) => {
    const newStatus = entry.status === 'active' ? 'inactive' : 'active';
    setActionLoading(entry.templateId);
    try {
      await updateLibraryEntryStatus(entry.templateId, newStatus);
      addFlash('success', `Entry ${entry.templateId} ${newStatus === 'active' ? 'reactivated' : 'deactivated'}`);
      await loadEntries();
    } catch (e: any) {
      addFlash('error', e.message || `Failed to update status`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(deleteTarget.templateId);
    try {
      await deleteLibraryEntry(deleteTarget.templateId);
      addFlash('success', `Entry ${deleteTarget.templateId} deleted`);
      setDeleteTarget(null);
      await loadEntries();
    } catch (e: any) {
      addFlash('error', e.message || 'Failed to delete entry');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveNotes = async (templateId: string, notes: string) => {
    try {
      await updateLibraryEntryNotes(templateId, notes);
      // Update local state immediately for responsiveness
      setEntries((prev) => prev.map((e) => e.templateId === templateId ? { ...e, notes } : e));
      addFlash('success', 'Notes saved');
    } catch (e: any) {
      addFlash('error', e.message || 'Failed to save notes');
    }
  };

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Manage stored column mapping templates">
        Approval Library
      </Header>

      {flashItems.length > 0 && (
        <Flashbar
          items={flashItems.map((item) => ({
            ...item,
            dismissible: true,
            onDismiss: () => setFlashItems((prev) => prev.filter((f) => f.id !== item.id)),
          }))}
        />
      )}

      {error && (
        <Flashbar
          items={[{ type: 'error', content: error, dismissible: true, onDismiss: () => setError(null), id: 'load-error' }]}
        />
      )}

      <Table
        items={sortedEntries}
        loading={loading}
        loadingText="Loading library entries"
        sortingColumn={{ sortingField: 'approvalDate' }}
        sortingDescending={sortDescending}
        onSortingChange={({ detail }) => {
          setSortDescending(detail.isDescending ?? true);
        }}
        columnDefinitions={[
          {
            id: 'templateId',
            header: 'Template ID',
            cell: (item: LibraryEntryListItem) => item.templateId,
            sortingField: 'templateId',
          },
          {
            id: 'approvalDate',
            header: 'Approval Date',
            cell: (item: LibraryEntryListItem) => new Date(item.approvalDate).toLocaleString(),
            sortingField: 'approvalDate',
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item: LibraryEntryListItem) => (
              <StatusIndicator type={item.status === 'active' ? 'success' : 'stopped'}>
                {item.status}
              </StatusIndicator>
            ),
          },
          {
            id: 'originJob',
            header: 'Origin Job',
            cell: (item: LibraryEntryListItem) => (
              <Link onFollow={() => navigate(`/jobs/${item.originJobId}`)}>
                {item.originJobId}
              </Link>
            ),
          },
          {
            id: 'accountType',
            header: 'Account Type',
            cell: (item: LibraryEntryListItem) => item.accountType,
          },
          {
            id: 'notes',
            header: 'Notes',
            cell: (item: LibraryEntryListItem) => (
              <NotesCell item={item} onSave={handleSaveNotes} />
            ),
            minWidth: 200,
          },
          {
            id: 'headerCount',
            header: 'Headers',
            cell: (item: LibraryEntryListItem) => item.headerCount,
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item: LibraryEntryListItem) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Link
                  onFollow={(e) => { e.preventDefault(); handleToggleStatus(item); }}
                  variant="primary"
                >
                  {actionLoading === item.templateId ? '...' : item.status === 'active' ? 'Deactivate' : 'Reactivate'}
                </Link>
                <Box color="text-status-inactive">|</Box>
                <Link
                  onFollow={(e) => { e.preventDefault(); setDeleteTarget(item); }}
                  variant="primary"
                >
                  {actionLoading === item.templateId ? '...' : 'Delete'}
                </Link>
              </SpaceBetween>
            ),
          },
        ]}
        empty={
          <Box textAlign="center" padding="l">
            No library entries found
          </Box>
        }
      />

      <Modal
        visible={deleteTarget !== null}
        onDismiss={() => setDeleteTarget(null)}
        header="Confirm deletion"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button
                variant="primary"
                loading={actionLoading === deleteTarget?.templateId}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Are you sure you want to permanently delete template{' '}
        <strong>{deleteTarget?.templateId}</strong>? This action cannot be undone.
      </Modal>
    </SpaceBetween>
  );
};

export default ApprovalLibrary;
